import React, { useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import LoadingScreen from '@/shared/components/feedback/LoadingScreen';

interface AuthRedirectHandlerProps {
  children: React.ReactNode;
}

/**
 * AuthRedirectHandler Component
 * 
 * Handles redirecting unauthenticated users from the portal domain to the accounts domain
 * This ensures that anyone hitting portal.recurringvaluepodcast.com without being logged in
 * gets redirected to accounts.recurringvaluepodcast.com for authentication
 */
const AuthRedirectHandler: React.FC<AuthRedirectHandlerProps> = ({ children }) => {
  const { isSignedIn, isLoaded } = useAuth();

  useEffect(() => {
    // Only proceed if auth has finished loading
    if (!isLoaded) return;

    // Check if we're on the portal domain and user is not signed in
    const isPortalDomain = window.location.hostname === 'portal.recurringvaluepodcast.com';
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname.includes('replit');
    
    // If on portal domain and not signed in, redirect to accounts domain
    if (isPortalDomain && !isSignedIn) {
      console.log('[AuthRedirectHandler] Redirecting unauthenticated user to accounts domain');
      
      // Preserve the current path so user can be redirected back after login
      const currentPath = window.location.pathname + window.location.search + window.location.hash;
      const returnUrl = encodeURIComponent(`https://portal.recurringvaluepodcast.com${currentPath}`);
      
      // Redirect to accounts domain with return URL
      window.location.href = `https://accounts.recurringvaluepodcast.com?redirect_url=${returnUrl}`;
      return;
    }

    // For local development, show a warning if not authenticated but don't redirect
    if (isLocalDev && !isSignedIn) {
      console.log('[AuthRedirectHandler] Local development - user not authenticated but not redirecting');
    }
  }, [isLoaded, isSignedIn]);

  // Show loading screen while auth is initializing
  if (!isLoaded) {
    return <LoadingScreen />;
  }

  // If we're on portal domain and not signed in, show loading screen while redirecting
  const isPortalDomain = window.location.hostname === 'portal.recurringvaluepodcast.com';
  if (isPortalDomain && !isSignedIn) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingScreen />
          <p className="mt-4 text-gray-600">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  // Render children if authenticated or in development
  return <>{children}</>;
};

export default AuthRedirectHandler; 